import { useCallback, useEffect, useRef, useState } from 'react';
import { createCache, putImage, removeImage, discardCache } from '../../lib/eft/aniCache';

/**
 * Staging a capture on the backend while it is still being taken.
 *
 * The capture grid holds each impression as base64 in `prints`. This watches that map and sends the
 * difference: a new impression is uploaded, a re-taken one replaces its slot, a cleared one is removed.
 * By the time the operator reaches Review, everything is already on the server and the validation step
 * has nothing left to transfer.
 *
 * Two rules govern everything here.
 *
 * Capture never waits. Uploads are fired off and their outcome recorded; a scanner has a person
 * standing at it and must not stall on a round trip. A failed upload is not an error the operator has
 * to deal with — it is retried on the next change, and the review step reports anything still missing.
 *
 * Nothing races. Prints arrive from an async capture loop, so which upload is "current" is tracked in a
 * ref written synchronously, not in state read inside a closure — the same reason the capture loop
 * itself keeps its position in a ref.
 */

export type SlotState = 'uploading' | 'staged' | 'failed';

interface Options {
  /** Off for card conversion, which has no live capture and no fingerprints to stage. */
  enabled: boolean;
  captureType: string | undefined;
  /** scanType -> base64 image, straight from the capture grid. */
  prints: Record<string, string>;
  /** scanType -> EBTS finger position. A print with no position cannot be placed in a record. */
  fgpOf: (slot: string) => number | undefined;
  /** Record type the impressions belong in. Type-4 for the live-capture workflows. */
  recordType: number;
}

export interface AniCacheState {
  cacheId: string | null;
  /** Per-slot upload state, for a quiet indicator on the capture grid. */
  slots: Record<string, SlotState>;
  /** Slots captured but not yet on the server. Review waits on this reaching zero. */
  pending: number;
  failed: string[];
  /** Discard the staged capture. Called on submit and on abandon. */
  discard: () => void;
  /**
   * Take over a cache the server already filled — an imported EFT.
   *
   * `key` must be the key the capture grid uses (the scan position), because that is what the watcher
   * diffs against. `serverSlot` is what the same impression is filed under in the cache, which the
   * import names after the record type and finger. Registering the server's name as the key made every
   * imported print look new: each was re-uploaded under a second name and the original queued for
   * deletion, leaving two copies of one capture and a stale reference behind.
   */
  adopt: (cacheId: string, staged: { key: string; serverSlot: string; data: string }[]) => void;
}

export function useAniCache(opts: Options): AniCacheState {
  const { enabled, captureType, prints, fgpOf, recordType } = opts;
  const [cacheId, setCacheId] = useState<string | null>(null);
  const [slots, setSlots] = useState<Record<string, SlotState>>({});

  /*
   * What has been sent, and what its bytes were. Compared against `prints` to find the difference, and
   * held in a ref rather than state because the effect below must see the value written by the previous
   * pass, not the one React has re-rendered with.
   */
  const sent = useRef<Map<string, { data: string; serverSlot: string }>>(new Map());
  const cacheRef = useRef<string | null>(null);
  const creating = useRef<Promise<string> | null>(null);

  /** One cache per capture session, created on the first impression rather than on mount. */
  const ensureCache = useCallback(async (): Promise<string> => {
    if (cacheRef.current) return cacheRef.current;
    if (!creating.current) {
      creating.current = createCache(captureType!).then((id) => {
        cacheRef.current = id;
        setCacheId(id);
        return id;
      });
      // A failed create must not poison every later attempt.
      creating.current.catch(() => { creating.current = null; });
    }
    return creating.current;
  }, [captureType]);

  useEffect(() => {
    if (!enabled || !captureType) return;

    const current = new Set(Object.keys(prints).filter((s) => prints[s]));

    // Gone from the grid: the operator cleared or re-took it. Drop the staged copy so the server's
    // view cannot include an impression the operator has discarded.
    for (const [slot, entry] of [...sent.current.entries()]) {
      if (current.has(slot)) continue;
      sent.current.delete(slot);
      setSlots((s) => { const n = { ...s }; delete n[slot]; return n; });
      const id = cacheRef.current;
      // Removed by the name the CACHE knows it by, which is not the grid's name for an imported print.
      if (id) void removeImage(id, entry.serverSlot).catch(() => undefined);
    }

    for (const slot of current) {
      const data = prints[slot]!;
      if (sent.current.get(slot)?.data === data) continue;
      const fgp = fgpOf(slot);
      // No finger position means no record can hold it; the EFT builder ignores it too.
      if (fgp === undefined) continue;

      // Recorded BEFORE the upload starts, so a re-render mid-flight does not queue it twice.
      // A freshly captured print is filed under the grid's own key; only an import differs.
      sent.current.set(slot, { data, serverSlot: slot });
      setSlots((s) => ({ ...s, [slot]: 'uploading' }));

      void (async () => {
        try {
          const id = await ensureCache();
          await putImage(id, { slot, fgp, recordType, image: data });
          // The operator may have re-taken it while this was in flight; only claim success if the
          // bytes we sent are still the bytes on screen.
          setSlots((s) => (sent.current.get(slot)?.data === data ? { ...s, [slot]: 'staged' } : s));
        } catch {
          /*
           * Cleared rather than marked permanently failed: forgetting what was sent is what makes the
           * next pass retry it. The operator is told nothing — the review step is where an impression
           * that never made it is reported, and it can still be fixed there.
           */
          sent.current.delete(slot);
          setSlots((s) => ({ ...s, [slot]: 'failed' }));
        }
      })();
    }
  }, [enabled, captureType, prints, fgpOf, recordType, ensureCache]);

  const discard = useCallback(() => {
    const id = cacheRef.current;
    cacheRef.current = null;
    creating.current = null;
    sent.current.clear();
    setCacheId(null);
    setSlots({});
    // Best effort: the cache expires on its own, but a capture is biometric and should not sit around
    // for two days because a workflow was abandoned.
    if (id) void discardCache(id).catch(() => undefined);
  }, []);

  const adopt = useCallback((id: string, staged: { key: string; serverSlot: string; data: string }[]) => {
    cacheRef.current = id;
    creating.current = null;
    setCacheId(id);
    for (const { key, serverSlot, data } of staged) sent.current.set(key, { data, serverSlot });
    setSlots(Object.fromEntries(staged.map((x) => [x.key, 'staged' as const])));
  }, []);

  const entries = Object.entries(slots);
  return {
    cacheId,
    slots,
    pending: entries.filter(([, v]) => v === 'uploading').length,
    failed: entries.filter(([, v]) => v === 'failed').map(([k]) => k),
    discard,
    adopt,
  };
}
